import { LitElement, css, html } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { getLongestTransitionMS } from '../functions/longestCSSTransition';

/**
 * Clicking the "Close" button or in the empty "overlay" space will emit a bubbling `close` event that parents should respect
 */
@customElement('ep-dialog')
export class EPDialog extends LitElement {

  // @property({ type: Boolean, attribute: 'show-actions', reflect: true })
  // public showActions: boolean = false;

  @property({ attribute: false })
  showActions: boolean = false;

  @property({ type: Boolean, attribute: 'full-screen', reflect: true })
  public fullScreen: boolean = false;

  @property({ type: Boolean, attribute: 'open', reflect: true })
  public open: boolean = false;

  @property({ type: Boolean, attribute: 'shim', reflect: true })
  public shim: boolean = false;

  @property({ type: String, reflect: true })
  public width: string = '640px';

  @property({ type: String, reflect: true })
  public height: string = '480px';

  @query('slot[name=frame-actions]')
  frameActionsSlotElement?: HTMLSlotElement;

  @query('[part=control]')
  dialogElement?: HTMLDivElement;

  @query('[part=overlay]')
  overlayElement?: HTMLDivElement;

  render() {
    return html`
      <div part="positioning-region" show-full-screen=${this.fullScreen} ?is-open=${this.open} ?is-shimmed=${this.shim}>
        <div role="presentation" part="overlay" @click=${this._handleCloseClick}></div>
        <div role="dialog" part="control" style=${`--dialog-width: ${this.width}; --dialog-height: ${this.height};`}>
          <div class="safety-zone">
            <div class="frame-top" role="toolbar" part="frame-top">
              <div class="frame-top-title">
                <slot name="frame-title"></slot>
              </div>
              <slot name="close-button">
                <fluent-button style="align-self: flex-start;" appearance="stealth" @click=${this._handleCloseClick}>Close &#10006;</fluent-button>
              </slot>
            </div>
            <fluent-divider></fluent-divider>
            <div class="modal-content">
              <slot @sp-opened=${this._blockEventPropagation} @sp-closed=${this._blockEventPropagation}></slot>
            </div>
            ${
              this.showActions
              ? html`
                <fluent-divider></fluent-divider>
                <div class="frame-bottom" role="menubar" part="frame-bottom">
                  <slot name="frame-actions" @slotchange=${this._handleFrameActionsSlotChange}
                    @sp-opened=${this._blockEventPropagation}
                    @sp-closed=${this._blockEventPropagation}>
                    <!-- <fluent-button appearance="accent">Save</fluent-button>
                    <fluent-button appearance="neutral">Cancel</fluent-button> -->
                  </slot>
                </div>
              `
              : html`
              <div style="display: none;">
                <slot name="frame-actions" @slotchange=${this._handleFrameActionsSlotChange}
                  @sp-opened=${this._blockEventPropagation}
                  @sp-closed=${this._blockEventPropagation}>
                </slot>
              </div>
              `
            }
          </div>
        </div>
      </div>
    `
  }

  private _handleCloseClick(event: Event) {
    // compute the longest "transition" time spent on these 2 elements, so we can close the overlay after the animation has finished
    const delayForAnimation = Math.max(getLongestTransitionMS(this.dialogElement), getLongestTransitionMS(this.overlayElement));
    this.open = false;
    setTimeout(() => this.dispatchEvent(new Event('close', {bubbles: true, composed: true})), delayForAnimation);
  }

  private _handleFrameActionsSlotChange() {
    this.showActions = (this.frameActionsSlotElement?.assignedElements()?.length ?? 0) > 0;
  }

  private _blockEventPropagation(event: Event) {
    //console.log('attempt to block event: ', event.type, event);
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  private _handleSPOpened(event: Event) {
    //console.log('sp-opened?', event)
    this.open = true;
  }

  private _handleSPClosed(event: Event) {
    //console.log('sp-closed?', event)
    this.open = false;
  }

  public override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('sp-opened', this._handleSPOpened);
    this.addEventListener('sp-closed', this._handleSPClosed);
  }

  public override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('sp-opened', this._handleSPOpened);
    this.removeEventListener('sp-closed', this._handleSPClosed);
  }

  static styles = css`
    :host {
      display: block;
      --color-text-muted: hsla(0, 0%, 15%, 0.7);
      --transition-time-first: 0.3s 0s;
      --transition-time-second: 0.25s 0.16s;
    }

    [part=positioning-region][show-full-screen=true] {
      display: flex;
      justify-content: center;
      position: fixed;
      inset: 0px;
      overflow: auto;
      perspective: 1000px;
    }
    
    /* Just in case the positioning region is covering the screen while the dialog is closed, deny clicks on it. */
    [part=positioning-region][show-full-screen=true]:not([is-open]) {
      pointer-events: none;
    }

    [part=positioning-region][show-full-screen=false] {
      display: contents;
    }

    [part=positioning-region][show-full-screen=true] [part=overlay] {
      position: fixed;
      inset: 0px;
      background: rgba(0, 0, 0, 0.3);
      touch-action: none;
    }

    :not([is-open]) [part=overlay] {
      opacity: 0.0;
      transition: opacity var(--transition-time-second);
    }
    [is-open] [part=overlay] {
      opacity: 1.0;
      transition: opacity var(--transition-time-first);
    }

    [part=positioning-region][show-full-screen=false] [part=overlay] {
      display: none;
    }
    [part=control] {
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.2), 0 calc(128 * 0.5px) calc((128 * 1px)) rgba(0, 0, 0, 0.24);
      margin-top: auto;
      margin-bottom: auto;
      border-radius: 8px;
      width: var(--dialog-width);
      height: var(--dialog-height);
      background: #fbfbfb;
      z-index: 1;
      border: 1px solid transparent;
    }
    [is-shimmed] [part=control] {
      box-shadow: none;
      margin: none;
      border: none;
    }

    [part=positioning-region]:not([is-open]) [part=control] {
      opacity: 0.0;
      transform: translateY(20px);
      transition: opacity var(--transition-time-first), transform var(--transition-time-first);
    }
    [is-open] [part=control] {
      opacity: 1.0;
      /* transform: translateY(0); */
      transition: opacity var(--transition-time-second), transform var(--transition-time-second);
    }

    .safety-zone {
      display: flex;
      flex-direction: column;
      height: 100%;
      border-radius: 8px;
      box-sizing: border-box;
    }

    .frame-top, .frame-bottom {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 0.25rem;
      margin: 0;
    }

    .frame-top .frame-top-title {
      flex-grow: 1;
      padding-left: 1rem;
    }

    .frame-bottom {
      justify-content: flex-end;
      gap: 5px;
      padding: 10px;
    }

    .modal-content {
      height: 100%;
      overflow: auto;
      /* scrollbar-gutter: stable both-edges; */
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-dialog': EPDialog
  }
}
